<template>
  <nav
    id="main-navigation"
    class="tw-navbar-shadow tw-relative tw-w-full tw-bg-white tw-p-4"
  >
    <!--Main navigation (desktop, laptop)-->
    <div class="tw-flex tw-items-center tw-justify-between">
      <!--Left side of the navigation-->
      <div class="tw-flex tw-items-center tw-space-x-8">
        <component
          :is="isUserLoggedIn ? 'InertiaLink' : 'a'"
          :href="isUserLoggedIn ? '/dashboard' : 'https://growthmentor.com'"
        >
          <img
            class="tw-image-crisp-edges tw-w-40 tw-p-1"
            src="@/assets/images/growthmentor-logo.png"
            alt="GrowthMentor Logo"
          />
        </component>

        <nav class="tw-hidden lg:tw-block">
          <ul class="tw-flex tw-items-center">
            <li
              v-for="(item, index) in mainNavigation"
              :key="index"
              class="tw-mr-2 tw-text-sm tw-font-semibold"
              :class="[
                item.isInMoreButton
                  ? 'tw-hidden min-[1270px]:tw-inline-block'
                  : 'tw-inline-block',
              ]"
            >
              <!--Item without a dropdown-->
              <InertiaLink
                v-if="!item.dropdown"
                :href="item.href[accessControl.actorType]"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-px-3 tw-py-2 tw-text-neutral-900 tw-transition-colors hover:tw-bg-neutral-50 focus:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-neutral-200"
              >
                <component :is="item.icon" class="tw-mr-2 tw-h-4 tw-w-4" />
                {{ item.name }}
              </InertiaLink>

              <!--Item with a dropdown-->
              <NavbarDropdown v-else :name="item.name" :items="item.dropdown">
                <template #icon>
                  <component
                    :is="item.icon"
                    class="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0"
                  />
                </template>
              </NavbarDropdown>
            </li>
            <li
              v-if="mainNavigationItemsInMoreButton.length"
              class="tw-text-sm min-[1270px]:tw-hidden"
            >
              <NavbarDropdown
                name="More"
                :items="mainNavigationItemsInMoreButton"
              >
                <template #icon>
                  <DotsIcon class="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0" />
                </template>
              </NavbarDropdown>
            </li>
          </ul>
        </nav>
      </div>

      <!--Right side of the navigation-->
      <div class="tw-flex tw-items-center">
        <div v-if="isUserLoggedIn" class="tw-flex tw-items-center tw-space-x-4">
          <BaseButton
            size="small"
            href="/search"
            is-inertia-link
            class="tw-hidden sm:tw-block"
          >
            Browse mentors
          </BaseButton>
          <div class="tw-flex tw-items-center tw-space-x-2">
            <NavbarIconItem
              :has-red-indicator="!!unreadMessages.length"
              :indicator-value="unreadMessages.length"
              screen-reader-text="View messages"
              href="/conversations"
              @click="markNotificationsAsRead('messages')"
              test_id="messages-icon"
            >
              <MessageIcon
                class="tw-h-6 tw-w-6 tw-stroke-[1.5px] lg:tw-h-4 lg:tw-w-4 lg:tw-stroke-1"
              />
            </NavbarIconItem>
            <NavbarNotificationsButton />
          </div>

          <NavbarUserDropdown
            :navigation="dropdownNavigation"
            class="tw-hidden lg:tw-inline-flex"
          />
        </div>

        <div v-else class="tw-hidden tw-items-center tw-space-x-2 lg:tw-flex">
          <BaseButton size="small" href="/search">Browse mentors</BaseButton>
          <BaseButton
            size="small"
            color="outline-secondary"
            href="/mentees/sign_up"
          >
            Sign up
          </BaseButton>
          <BaseButton size="small" color="primary" href="/users/sign_in">
            Log in
          </BaseButton>
        </div>

        <!--Navigation hamburger-->
        <NavbarHamburgerButton
          class="tw-ml-4 tw-p-4 lg:tw-hidden"
          :is-open="isMobileNavigationOpen"
          @click="toggleMobileNavigation"
        />
      </div>
    </div>

    <!--Mobile navigation (phones, tablets)-->
    <MobileNavbar
      :is-open="isMobileNavigationOpen"
      :navigation="mainNavigation"
      :user-dropdown-navigation="dropdownNavigation"
    />
  </nav>
</template>

<script setup>
import BaseButton from '../BaseButton.vue'
import NavbarIconItem from './NavbarIconItem.vue'
import NavbarUserDropdown from './NavbarUserDropdown.vue'
import NavbarHamburgerButton from './NavbarHamburgerButton.vue'
import NavbarDropdown from './NavbarDropdown.vue'
import MessageIcon from '@/assets/images/icons/message.svg'
import DotsIcon from '@/assets/images/icons/dots.svg'
import MobileNavbar from './MobileNavbar.vue'
import { router } from '@inertiajs/vue3'
import NavbarNotificationsButton from '@/shared/components/navbar/NavbarNotificationsButton.vue'
import { PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL } from '@/shared/data/actionCableChannels.js'
import { onMounted, ref } from 'vue'
import { useNavigationElements } from '@/shared/composables/navigation/useNavigationElements.js'
import { useActionCable } from '@/shared/composables/useActionCable.ts'
import { useDebugger } from '@/shared/composables/useDebugger.js'
import axios from 'axios'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'

const { $debug } = useDebugger()
const { isUserLoggedIn, accessControl } = usePermissionSystem()
const { mainNavigation, mainNavigationItemsInMoreButton, dropdownNavigation } =
  useNavigationElements()
const { subscribeChannel } = useActionCable({
  channelName: PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL,
  onConnected: () => {
    $debug(`[AC Channel: ${PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL}] connected`)
  },
  onReceived: (data) => {
    $debug(
      `[AC Channel: ${PERSONAL_MESSAGE_NOTIFICATIONS_CHANNEL}] data received`,
    )
    const notifications = JSON.parse(data.notifications)
    unreadMessages.value = notifications.filter(
      (notification) => !notification.read_at,
    )
  },
})

const isMobileNavigationOpen = ref(false)
const unreadMessages = ref([])

const toggleMobileNavigation = () => {
  isMobileNavigationOpen.value = !isMobileNavigationOpen.value
}

const markNotificationsAsRead = (notificationsType) => {
  axios.post('/notifications/mark_all_as_read_by_type', {
    type: notificationsType,
  })
  unreadMessages.value = []
}

onMounted(() => {
  // Hide navigation when user changes page
  router.on('success', () => {
    isMobileNavigationOpen.value = false
  })

  subscribeChannel()
})
</script>
